import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './OrderSuccessPage.css';

function OrderSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to get order details from multiple sources
    const loadOrderDetails = () => {
      // 1. Check for order details passed via navigation state
      if (location.state?.orderDetails) {
        setOrderDetails(location.state.orderDetails);
        setLoading(false);
        return;
      }

      // 2. Check for session_id from Stripe redirect
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        // Try to get order from sessionStorage
        const pendingOrder = sessionStorage.getItem('pendingOrder');
        if (pendingOrder) {
          try {
            const orderData = JSON.parse(pendingOrder);
            setOrderDetails(orderData);
            // Clear the session storage after retrieving
            sessionStorage.removeItem('pendingOrder');
            setLoading(false);
            return;
          } catch (error) {
            console.error('Error parsing pending order:', error);
          }
        }
      }

      // 3. If no order data found, set to null (will show message)
      setOrderDetails(null);
      setLoading(false);
    };

    loadOrderDetails();
  }, [location.state, searchParams]);

  if (loading) {
    return (
      <div className="order-success-page">
        <div className="success-container">
          <div className="success-content">
            <h1 className="thank-you-heading">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="order-success-page">
        <div className="success-container">
          <div className="success-content">
            <h1 className="thank-you-heading">Order Received!</h1>
            <p className="confirmation-text">
              Thank you for your purchase. You will receive an order confirmation email shortly.
            </p>
            <button 
              onClick={() => navigate('/shop')} 
              className="continue-shopping-btn"
              style={{ marginTop: '2rem' }}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-success-page">
      <div className="success-container">
        <div className="success-content">
          <h1 className="thank-you-heading">Thank you!</h1>
          
          <p className="confirmation-text">
            You will receive an order confirmation email shortly...
          </p>
          
          <p className="ticket-notice">
            *if you have purchased tickets for an event, they will arrive in a separate email.
          </p>

          {orderDetails && (
            <>
              <div className="order-details-grid">
                {/* Shipping Address Section - Left Column */}
                {orderDetails.shippingAddress && (
                  <div className="shipping-section">
                    <h2 className="section-title">SHIPPING ADDRESS</h2>
                    <div className="shipping-address-box">
                      <p className="address-name">
                        {orderDetails.shippingAddress.firstName} {orderDetails.shippingAddress.lastName}
                      </p>
                      <p className="address-line">{orderDetails.shippingAddress.address}</p>
                      <p className="address-line">
                        {orderDetails.shippingAddress.postalCode} {orderDetails.shippingAddress.city}
                      </p>
                      <p className="address-line">{orderDetails.shippingAddress.country}</p>
                    </div>
                  </div>
                )}

                {/* Order Summary Section - Right Column */}
                <div className="summary-section">
                  <h2 className="section-title">ORDER SUMMARY</h2>
                  
                  <div className="order-items">
                {orderDetails.items && orderDetails.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-info">
                      <h3>{item.name}</h3>
                      {item.category && <p className="item-category">{item.category}</p>}
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                    </div>
                    <div className="item-price">
                      {item.totalPrice ? `${item.totalPrice.toFixed(2)} SEK` : 
                       item.finalPrice && item.quantity ? `${(item.finalPrice * item.quantity).toFixed(2)} SEK` :
                       item.price ? `${(item.price * item.quantity).toFixed(2)} SEK` : 'N/A'}
                    </div>
                  </div>
                ))}
                  </div>

                  <div className="order-total">
                    {/* Show subtotal if postage exists */}
                    {orderDetails.postageTotal > 0 && (
                      <div className="total-row" style={{ borderBottom: '1px solid #ccc', paddingBottom: '10px', marginBottom: '10px' }}>
                        <p className="total-label" style={{ fontSize: '1.2rem', fontWeight: 'normal' }}>Subtotal:</p>
                        <p className="total-amount" style={{ fontSize: '1.2rem', fontWeight: 'normal' }}>
                          {orderDetails.subtotal ? `${orderDetails.subtotal.toFixed(2)} SEK` : 
                           orderDetails.finalTotal && orderDetails.postageTotal ? 
                           `${(orderDetails.finalTotal - orderDetails.postageTotal).toFixed(2)} SEK` : 'N/A'}
                        </p>
                      </div>
                    )}
                    
                    {/* Show postage if it exists */}
                    {orderDetails.postageTotal > 0 && (
                      <div className="total-row" style={{ borderBottom: '1px solid #ccc', paddingBottom: '10px', marginBottom: '10px' }}>
                        <p className="total-label" style={{ fontSize: '1.2rem', fontWeight: 'normal' }}>Postage:</p>
                        <p className="total-amount" style={{ fontSize: '1.2rem', fontWeight: 'normal' }}>
                          {orderDetails.postageTotal.toFixed(2)} SEK
                        </p>
                      </div>
                    )}
                    
                    {/* Grand total */}
                    <div className="total-row">
                      <p className="total-label">TOTAL:</p>
                      <p className="total-amount">
                        {orderDetails.finalTotal ? `${orderDetails.finalTotal.toFixed(2)} SEK` : 
                         orderDetails.total ? `${orderDetails.total.toFixed(2)} SEK` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Continue Shopping Button - Below both columns */}
              <button 
                onClick={() => navigate('/shop')} 
                className="continue-shopping-btn"
              >
                Continue Shopping
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderSuccessPage;
